// /app/brands/$brandId — Brand cockpit.
//
// Ported visually from design-handoff/src/pages/brand.jsx (475-line
// 7-tab handoff). Per design rules: inline style={{}} + var(--*) CSS
// vars + tokens.css utility classes only — NO Tailwind on this route.
//
// Tab order matches the handoff exactly:
//   Overview · Posts · Brand Kit · Voice · Knowledge · Assets · Accounts
//
// API wiring (preserved from prior implementation):
//   GET    /v1/brands/:id                    → cockpit header
//   GET    /v1/brands/:id/health             → KPI strip + missing-signals
//   GET    /v1/brands/:id/posts              → Overview "upcoming" + Posts tab
//   GET    /v1/brands/:id/assets             → Assets tab
//   GET    /v1/brands/:id/social-accounts    → Overview "Connected" + Accounts tab
//   GET    /v1/brands/:id/kit                → KitTab
//   GET    /v1/brands/:id/kb                 → KnowledgeTab
//   PATCH  /v1/brands/:id                    → Voice tab + Settings tab
//
// The active-brand store live-tints --accent + --accent-soft as soon as
// the brand resolves (see useActiveBrand.setActive).

import { useEffect, useState, type CSSProperties } from "react";
import { createFileRoute, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type CursorPage } from "@/lib/api";
import { useActiveBrand } from "@/stores/active-brand";
import type { Brand, Post, SocialAccount } from "@/lib/types";
import { BrandMark, PlatformChip, Tabs, Badge, hexToRgba } from "@/components/primitives";
import { Icons } from "@/components/icons";
import { KitTab } from "@/components/brand-cockpit/KitTab";
import { VoiceTab } from "@/components/brand-cockpit/VoiceTab";
import { PostsTab } from "@/components/brand-cockpit/PostsTab";
import { KnowledgeTab } from "@/components/brand-cockpit/KnowledgeTab";
import { AssetsTab } from "@/components/brand-cockpit/AssetsTab";
import { AccountsTab } from "@/components/brand-cockpit/AccountsTab";
import { usePostDrawer } from "@/components/PostDrawer";

// Backend v0.5.35 response shape from GET /v1/brands/{id}/health.
interface BrandHealth {
  brand_id: string;
  score: number;
  reasons: Array<{
    id: "palette" | "typography" | "voice" | "kb" | "social" | string;
    label: string;
    met: boolean;
    score: number;
  }>;
}

type TabId = "overview" | "posts" | "kit" | "voice" | "knowledge" | "assets" | "accounts";

const TAB_IDS: TabId[] = ["overview", "posts", "kit", "voice", "knowledge", "assets", "accounts"];

export const Route = createFileRoute("/app/brands/$brandId")({
  component: BrandCockpit,
  // Accept ?tab=<tabId> so deep-links like the BrandHealthGuide modal can
  // open straight into the right cockpit pane. Also accept ?meta_pick=
  // (Meta OAuth callback brings the operator back with the state token
  // so AccountsTab can fetch the pending pages + show the picker modal)
  // and ?meta_error= for failed callbacks.
  validateSearch: (search: Record<string, unknown>): {
    tab?: TabId;
    meta_pick?: string;
    meta_error?: string;
  } => {
    const out: { tab?: TabId; meta_pick?: string; meta_error?: string } = {};
    const t = search.tab;
    if (typeof t === "string" && (TAB_IDS as readonly string[]).includes(t)) {
      out.tab = t as TabId;
    }
    if (typeof search.meta_pick === "string" && search.meta_pick) {
      out.meta_pick = search.meta_pick;
    }
    if (typeof search.meta_error === "string" && search.meta_error) {
      out.meta_error = search.meta_error;
    }
    return out;
  },
});

const NEXUS_RED = "#E02D1C";

function BrandCockpit() {
  const { brandId } = useParams({ from: "/app/brands/$brandId" });
  const search = useSearch({ from: "/app/brands/$brandId" });
  // Seed tab from ?tab= if provided (deep-link from BrandHealthGuide etc.),
  // otherwise default to overview.
  const [tab, setTab] = useState<TabId>(search.tab ?? "overview");
  const setActive = useActiveBrand((s) => s.setActive);
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";

  const { data: brand, isLoading } = useQuery({
    queryKey: ["brand", brandId],
    queryFn: () => api<Brand>(`/brands/${brandId}`),
    staleTime: 30_000,
  });
  const { data: health } = useQuery({
    queryKey: ["brand", brandId, "health"],
    queryFn: () => api<BrandHealth>(`/brands/${brandId}/health`),
    staleTime: 60_000,
    enabled: !!brand,
  });

  // Live-tint chrome when the brand resolves; reset back to Nexus red on unmount.
  useEffect(() => {
    if (brand) setActive(brand);
    return () => {
      // Reset on unmount so leaving the cockpit restores the workspace accent.
      // Other pages that need the accent will set it again.
      const root = document.documentElement;
      root.style.setProperty("--accent", NEXUS_RED);
      root.style.setProperty("--accent-soft", "rgba(224,45,28,0.12)");
    };
  }, [brand, setActive]);

  if (isLoading) return <CockpitSkeleton />;
  if (!brand) return <NotFound tr={tr} />;

  const accent = brand.accent_hex || NEXUS_RED;
  const accentSoft = brand.accent_soft_hex || hexToRgba(accent, 0.12);

  const TABS: { id: TabId; label: string; count?: number }[] = [
    { id: "overview", label: tr ? "Genel bakış" : "Overview" },
    { id: "posts", label: tr ? "Gönderiler" : "Posts" },
    { id: "kit", label: tr ? "Marka kiti" : "Brand Kit" },
    { id: "voice", label: tr ? "Ses" : "Voice" },
    { id: "knowledge", label: tr ? "Bilgi tabanı" : "Knowledge" },
    { id: "assets", label: tr ? "Görseller" : "Assets" },
    {
      id: "accounts",
      label: tr ? "Hesaplar" : "Accounts",
      count: brand.connected_platforms?.length ?? 0,
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <CockpitHeader
        brand={brand}
        health={health}
        accentSoft={accentSoft}
        tr={tr}
        tab={tab}
        onTab={setTab}
        tabs={TABS}
      />
      <div className="scroll-y" style={{ flex: 1, padding: "24px 32px 48px" }}>
        {tab === "overview" && (
          <BrandOverview
            brand={brand}
            health={health}
            accent={accent}
            accentSoft={accentSoft}
            tr={tr}
            setTab={setTab}
            onTabFromMissing={setTab}
          />
        )}
        {tab === "posts" && <PostsTab brandId={brandId} />}
        {tab === "kit" && <KitTab brandId={brandId} />}
        {tab === "voice" && <VoiceTab brandId={brandId} />}
        {tab === "knowledge" && <KnowledgeTab brandId={brandId} />}
        {tab === "assets" && <AssetsTab brandId={brandId} />}
        {tab === "accounts" && <AccountsTab brandId={brandId} />}
      </div>
    </div>
  );
}

// ─── Header (brand tinted top with accent halo, stats strip, tab bar) ──────

function CockpitHeader({
  brand,
  health,
  accentSoft,
  tr,
  tab,
  onTab,
  tabs,
}: {
  brand: Brand;
  health?: BrandHealth;
  accentSoft: string;
  tr: boolean;
  tab: TabId;
  onTab: (t: TabId) => void;
  tabs: { id: TabId; label: string; count?: number }[];
}) {
  const navigate = useNavigate();
  return (
    <div
      style={{
        position: "relative",
        overflow: "hidden",
        borderBottom: "1px solid var(--b1)",
        flexShrink: 0,
      }}
    >
      {/* Soft accent halo top-right */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: -100,
          right: -100,
          width: 360,
          height: 360,
          background: `radial-gradient(circle, ${accentSoft}, transparent 60%)`,
          filter: "blur(30px)",
          pointerEvents: "none",
        }}
      />

      <div className="cockpit-head" style={{ position: "relative", padding: "28px 32px 0" }}>
        <div className="cockpit-id" style={{ display: "flex", alignItems: "flex-start", gap: 18, marginBottom: 24 }}>
          <BrandMark brand={brand} size={56} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>
              {tr ? "05 · Marka kokpiti" : "05 · Brand cockpit"}
            </div>
            <h1 style={{ fontSize: 32 }}>{brand.trade_name}</h1>
            <div
              style={{
                display: "flex",
                gap: 12,
                marginTop: 8,
                fontSize: 13,
                color: "var(--tx2)",
                flexWrap: "wrap",
              }}
            >
              <span>{brand.industry ?? (tr ? "Sektör belirtilmemiş" : "Industry —")}</span>
              <span style={{ color: "var(--mu)" }}>·</span>
              <span>{(brand.primary_language ?? "tr").toUpperCase()}</span>
              <span style={{ color: "var(--mu)" }}>·</span>
              <span>
                {brand.connected_platforms?.length ?? 0}{" "}
                {tr ? "platform bağlı" : "platforms connected"}
              </span>
            </div>
          </div>
          <div className="cockpit-actions" style={{ display: "flex", gap: 8 }}>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => onTab("kit")}
              type="button"
            >
              <Icons.Edit size={13} />
              {tr ? "Markayı düzenle" : "Edit brand"}
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => navigate({ to: "/app/generate" })}
              type="button"
            >
              <Icons.Sparkles size={13} />
              {tr ? "Üret" : "Generate"}
            </button>
          </div>
        </div>

        {/* Stats strip — 4 brand-level KPIs */}
        <div
          className="cockpit-stats"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 12,
            marginBottom: 20,
          }}
        >
          <BrandStat
            label={tr ? "Bu hafta" : "This week"}
            value={brand.scheduled_count ?? 0}
            sub={tr ? "planlanan" : "scheduled"}
          />
          <BrandStat
            label={tr ? "Onay bekleyen" : "Pending"}
            value={brand.pending_count ?? 0}
            sub={
              (brand.pending_count ?? 0) > 0
                ? tr
                  ? "ilgilen"
                  : "needs you"
                : tr
                  ? "tertemiz"
                  : "all clear"
            }
            accent={(brand.pending_count ?? 0) > 0}
          />
          <BrandStat
            label={tr ? "Sağlık" : "Health"}
            value={health ? `${health.score}` : "—"}
            sub={
              health
                ? health.score >= 70
                  ? tr
                    ? "sağlıklı"
                    : "healthy"
                  : tr
                    ? "incele"
                    : "review"
                : tr
                  ? "hesaplanıyor"
                  : "computing"
            }
            accent={!!health && health.score < 70}
          />
          <BrandStat
            label={tr ? "Son 7 gün" : "Last 7d"}
            value={brand.posts_this_week ?? 0}
            sub={tr ? "yayınlanan" : "published"}
          />
        </div>

        <Tabs<TabId> value={tab} onChange={onTab} tabs={tabs} />
      </div>
    </div>
  );
}

function BrandStat({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string | number;
  sub: string;
  accent?: boolean;
}) {
  return (
    <div
      style={{
        padding: 16,
        background: "var(--surface)",
        border: `1px solid ${accent ? "var(--accent)" : "var(--b2)"}`,
        borderRadius: 10,
      }}
    >
      <div
        className="mono"
        style={{
          fontSize: 10,
          color: "var(--mu)",
          letterSpacing: "0.15em",
          textTransform: "uppercase",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span
          className="t-num"
          style={{ fontSize: 26, color: accent ? "var(--accent)" : "var(--tx)" }}
        >
          {value}
        </span>
        <span style={{ fontSize: 11, color: "var(--tx2)" }}>{sub}</span>
      </div>
    </div>
  );
}

// ─── Overview tab content ──────────────────────────────────────────────────

function BrandOverview({
  brand,
  health,
  accent,
  accentSoft,
  tr,
  onTabFromMissing,
  setTab,
}: {
  brand: Brand;
  health?: BrandHealth;
  accent: string;
  accentSoft: string;
  tr: boolean;
  onTabFromMissing: (t: TabId) => void;
  setTab: (t: TabId) => void;
}) {
  // Pull a small slice of posts + social accounts so Overview can show
  // "upcoming" + "connected platforms" without re-implementing those
  // calls. Both are kept at small limits and live-cached.
  const { data: postsPage } = useQuery({
    queryKey: ["brand", brand.id, "posts", "overview"],
    queryFn: () =>
      api<CursorPage<Post>>(`/posts?brand=${brand.id}&limit=6&status=scheduled`),
    staleTime: 30_000,
  });
  const upcoming = postsPage?.data ?? [];

  const { data: accountsData } = useQuery({
    queryKey: ["brand", brand.id, "social-accounts"],
    queryFn: () =>
      api<{ data: SocialAccount[] } | { social_accounts: SocialAccount[] }>(
        `/brands/${brand.id}/social-accounts`,
      ),
    staleTime: 30_000,
  });
  const accounts: SocialAccount[] =
    (accountsData && "data" in accountsData
      ? accountsData.data
      : accountsData && "social_accounts" in accountsData
        ? accountsData.social_accounts
        : []) ?? [];

  const missing = health?.reasons.filter((r) => !r.met) ?? [];
  // Overall brand health as a 0-100 % — sum of all met signal points.
  // Backend already returns this in health.score; we just clamp.
  const healthScore = health ? Math.max(0, Math.min(100, health.score)) : 0;
  // Each signal maps to the cockpit tab that fixes it so we can render
  // a one-click "Fix X" button next to each missing item.
  const signalToTab: Record<string, TabId> = {
    palette: "kit",
    typography: "kit",
    voice: "voice",
    kb: "knowledge",
    social: "accounts",
  };

  return (
    <>
    <div className="cockpit-overview" style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 20 }}>
      {/* Left column */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Upcoming posts */}
        <div className="card" style={{ padding: 22 }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <div className="eyebrow" style={{ marginBottom: 4 }}>
                — {tr ? "Yaklaşan · 7 gün" : "Upcoming · 7 days"}
              </div>
              <h2 style={{ fontSize: 16 }}>
                {upcoming.length}{" "}
                {tr ? "planlı gönderi" : upcoming.length === 1 ? "post queued" : "posts queued"}
              </h2>
            </div>
            <button className="btn btn-secondary btn-sm" type="button">
              {tr ? "Takvim" : "Calendar"} →
            </button>
          </div>
          {upcoming.length === 0 ? (
            <div
              style={{
                padding: "32px 12px",
                color: "var(--mu)",
                fontSize: 13,
                textAlign: "center",
              }}
            >
              {tr
                ? "Bu marka için planlanmış gönderi yok."
                : "No scheduled posts for this brand."}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {upcoming.map((p) => (
                <UpcomingPostRow key={p.id} post={p} tr={tr} />
              ))}
            </div>
          )}
        </div>

        {/* Setup / missing signals */}
        {missing.length > 0 ? (
          <div className="card" style={{ padding: 22 }}>
            <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
              <div className="eyebrow" style={{ flex: 1 }}>
                — {tr ? "Kurulum eksikleri" : "Setup checklist"}
              </div>
              <span className="mono" style={{ fontSize: 11, color: "var(--mu)" }}>
                {missing.length} {tr ? "eksik" : "missing"} · {health!.score}/100
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              {missing.map((r, i) => (
                <div
                  key={r.id}
                  style={{
                    padding: "10px 0",
                    borderBottom: i < missing.length - 1 ? "1px dashed var(--b1)" : "none",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: accent,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ flex: 1, fontSize: 13 }}>{r.label}</span>
                  <span
                    className="mono"
                    style={{ fontSize: 11, color: "var(--mu)" }}
                  >
                    +{r.score}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {/* Right column */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Auto-publish toggle — workflow opt-in for bulk gens.
            Default OFF (drafts review). When ON, bulk children go
            straight to 'scheduled' without operator review. v0.1.67. */}
        <AutoPublishToggle brand={brand} tr={tr} />
        {/* Voice loop callout */}
        <div
          className="card"
          style={{
            padding: 20,
            background: `linear-gradient(135deg, ${accentSoft}, transparent)`,
            border: `1px solid ${hexToRgba(accent, 0.2)}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: accent,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icons.Mic size={14} />
            </div>
            <div className="eyebrow" style={{ color: accent }}>
              — {tr ? "Ses döngüsü" : "Voice loop"}
            </div>
          </div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              marginBottom: 6,
              fontFamily: "var(--display)",
            }}
          >
            {tr
              ? "AI markayı her onayda biraz daha öğreniyor"
              : "AI is learning your voice with every approval"}
          </div>
          <div style={{ fontSize: 12, color: "var(--tx2)", lineHeight: 1.6 }}>
            {tr
              ? "Daha fazla onay verdikçe üretimler markaya yakınlaşır. Ses sekmesinden tonu doğrudan ayarlayabilirsin."
              : "More approvals → tighter brand fit. Tune tone directly in the Voice tab."}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 14 }}>
            <button
              className="btn btn-secondary btn-sm"
              style={{ flex: 1, justifyContent: "center" }}
              type="button"
              onClick={() => setTab("voice")}
            >
              {tr ? "Sesi düzenle" : "Edit voice"}
            </button>
          </div>
        </div>

        {/* Connected platforms */}
        <div className="card" style={{ padding: 20 }}>
          <div className="eyebrow" style={{ marginBottom: 12 }}>
            — {tr ? "Bağlı hesaplar" : "Connected"}
          </div>
          {accounts.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--mu)", padding: "8px 0" }}>
              {tr
                ? "Henüz platform bağlanmamış."
                : "No platforms connected yet."}
            </div>
          ) : (
            accounts.map((a, i) => (
              <div
                key={a.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "8px 0",
                  borderBottom: i < accounts.length - 1 ? "1px dashed var(--b1)" : "none",
                }}
              >
                <PlatformChip platform={a.platform} size={26} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {a.display_name || a.username || a.platform}
                  </div>
                  <div
                    className="mono"
                    style={{
                      fontSize: 10,
                      color:
                        a.status === "active"
                          ? "var(--success)"
                          : a.status === "expired"
                            ? "var(--warning)"
                            : "var(--mu)",
                    }}
                  >
                    ●{" "}
                    {a.status === "active"
                      ? tr
                        ? "bağlı"
                        : "connected"
                      : a.status === "expired"
                        ? tr
                          ? "süresi doldu"
                          : "expired"
                        : tr
                          ? "bağlı değil"
                          : "disconnected"}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Brand health — overall score (0-100%) + per-signal missing list */}
        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <Icons.Folder size={14} style={{ color: accent }} />
            <span className="eyebrow">— {tr ? "Marka sağlığı" : "Brand health"}</span>
            <span
              style={{
                marginLeft: "auto",
                fontFamily: "var(--mono)",
                fontSize: 11,
                color: accent,
              }}
            >
              {healthScore}%
            </span>
          </div>
          <div
            style={{
              height: 6,
              background: "var(--elev)",
              borderRadius: 3,
              overflow: "hidden",
              marginBottom: 12,
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${Math.max(2, Math.min(100, healthScore))}%`,
                background: accent,
                transition: "width 250ms ease-out",
              }}
            />
          </div>
          {missing.length === 0 ? (
            <div style={{ fontSize: 12, color: "var(--success)", lineHeight: 1.5 }}>
              {tr
                ? "✓ Tüm sinyaller tamam. Üretimler tam markaya uygun çıkar."
                : "✓ All signals complete. Generations will fully match your brand."}
            </div>
          ) : (
            <>
              <div style={{ fontSize: 11, color: "var(--mu)", marginBottom: 8 }}>
                {tr
                  ? `${missing.length} eksik · her birini tamamla → daha iyi üretim:`
                  : `${missing.length} missing · finish each → better output:`}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {missing.map((r) => {
                  const targetTab = signalToTab[r.id];
                  return (
                    <div
                      key={r.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "6px 8px",
                        borderRadius: 6,
                        background: "var(--elev)",
                        border: "1px solid var(--b1)",
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: accent,
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ flex: 1, fontSize: 12, color: "var(--tx)" }}>
                        {r.label}
                      </span>
                      <span
                        className="mono"
                        style={{ fontSize: 10, color: "var(--mu)" }}
                      >
                        +{r.score}
                      </span>
                      {targetTab ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => onTabFromMissing?.(targetTab)}
                          style={{
                            fontSize: 11,
                            padding: "2px 8px",
                            height: 24,
                          }}
                        >
                          {tr ? "Düzelt →" : "Fix →"}
                        </button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    <DangerZone brand={brand} tr={tr} />
    </>
  );
}

// DangerZone — irreversible brand removal. Lives at the bottom of Overview
// per the operator request. Guarded by a type-the-name confirmation modal
// so an accidental click can't wipe a brand. On success the brand, its
// assets/files, social connections, and all generated content are purged
// server-side (DELETE /v1/brands/:id) and we route back to the brand list.
function DangerZone({ brand, tr }: { brand: Brand; tr: boolean }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");

  const del = useMutation({
    mutationFn: () => api<void>(`/brands/${brand.id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["brands"] });
      qc.invalidateQueries({ queryKey: ["workspace"] });
      navigate({ to: "/app/brands" });
    },
  });

  const expected = brand.trade_name || "";
  const matches = confirm.trim() === expected.trim() && expected.trim() !== "";

  return (
    <div
      className="card"
      style={{
        marginTop: 20,
        padding: 22,
        border: "1px solid var(--red-border)",
        background: "var(--red-muted)",
      }}
    >
      <div className="eyebrow" style={{ color: "var(--red)", marginBottom: 6 }}>
        — {tr ? "Tehlikeli bölge" : "Danger zone"}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
            {tr ? "Bu markayı sil" : "Delete this brand"}
          </div>
          <div style={{ fontSize: 12, color: "var(--tx2)", lineHeight: 1.6 }}>
            {tr
              ? "Marka, yüklenen ve üretilen tüm görseller, bağlı hesaplar ve tüm gönderi/üretim geçmişi kalıcı olarak silinir. Bu işlem geri alınamaz."
              : "Permanently removes the brand, all uploaded & generated images, connected accounts, and every post/generation. This cannot be undone."}
          </div>
        </div>
        <button
          type="button"
          className="btn"
          onClick={() => {
            setConfirm("");
            setOpen(true);
          }}
          style={{
            background: "var(--red)",
            color: "#fff",
            whiteSpace: "nowrap",
          }}
        >
          {tr ? "Markayı sil" : "Delete brand"}
        </button>
      </div>

      {open ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 16,
          }}
          onClick={() => !del.isPending && setOpen(false)}
        >
          <div
            className="card"
            style={{ padding: 24, maxWidth: 440, width: "100%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: 17, marginBottom: 8 }}>
              {tr ? "Markayı kalıcı olarak sil" : "Permanently delete brand"}
            </h2>
            <div
              style={{
                fontSize: 13,
                color: "var(--tx2)",
                lineHeight: 1.6,
                marginBottom: 16,
              }}
            >
              {tr ? (
                <>
                  Onaylamak için marka adını yaz:{" "}
                  <strong style={{ color: "var(--tx)" }}>{expected}</strong>
                </>
              ) : (
                <>
                  Type the brand name to confirm:{" "}
                  <strong style={{ color: "var(--tx)" }}>{expected}</strong>
                </>
              )}
            </div>
            <input
              autoFocus
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={expected}
              style={{
                width: "100%",
                padding: "10px 12px",
                marginBottom: 16,
                background: "var(--base)",
                border: "1px solid var(--b2)",
                borderRadius: 8,
                color: "var(--tx)",
                fontSize: 14,
              }}
            />
            {del.error ? (
              <div
                style={{
                  fontSize: 12,
                  color: "var(--red)",
                  marginBottom: 12,
                }}
              >
                {del.error instanceof Error
                  ? del.error.message
                  : tr
                    ? "Silme başarısız oldu."
                    : "Delete failed."}
              </div>
            ) : null}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={del.isPending}
                onClick={() => setOpen(false)}
              >
                {tr ? "Vazgeç" : "Cancel"}
              </button>
              <button
                type="button"
                className="btn"
                disabled={!matches || del.isPending}
                onClick={() => del.mutate()}
                style={{
                  background: matches ? "var(--red)" : "var(--elev)",
                  color: matches ? "#fff" : "var(--mu)",
                }}
              >
                {del.isPending
                  ? tr
                    ? "Siliniyor…"
                    : "Deleting…"
                  : tr
                    ? "Kalıcı olarak sil"
                    : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function UpcomingPostRow({ post, tr }: { post: Post; tr: boolean }) {
  const openDrawer = usePostDrawer((s) => s.open);
  const when = post.scheduled_for || post.published_at;
  return (
    <button
      onClick={() => openDrawer(post.id)}
      type="button"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 0",
        textAlign: "left",
        width: "100%",
        borderBottom: "1px dashed var(--b1)",
      }}
    >
      <PlatformChip platform={post.platform} size={26} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {post.text_body || (tr ? "(metin yok)" : "(no text)")}
        </div>
        <div
          className="mono"
          style={{ fontSize: 10, color: "var(--mu)", marginTop: 2 }}
        >
          {when ? formatTime(when, tr) : "—"}
        </div>
      </div>
      <Badge status={post.status} />
    </button>
  );
}

function formatTime(iso: string, tr: boolean): string {
  try {
    return new Date(iso).toLocaleString(tr ? "tr-TR" : "en-GB", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// ─── Loading + 404 states ──────────────────────────────────────────────────

function CockpitSkeleton() {
  const cellStyle: CSSProperties = {
    height: 90,
    background: "var(--surface)",
    border: "1px solid var(--b2)",
    borderRadius: 10,
  };
  return (
    <div style={{ padding: "28px 32px" }}>
      <div className="skel" style={{ height: 64, borderRadius: 10, marginBottom: 24 }} />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <div className="skel" style={cellStyle} />
        <div className="skel" style={cellStyle} />
        <div className="skel" style={cellStyle} />
        <div className="skel" style={cellStyle} />
      </div>
      <div
        className="skel"
        style={{ height: 240, borderRadius: 12, marginTop: 16 }}
      />
    </div>
  );
}

function NotFound({ tr }: { tr: boolean }) {
  return (
    <div style={{ padding: 32, textAlign: "center", color: "var(--tx2)" }}>
      <p>{tr ? "Marka bulunamadı." : "Brand not found."}</p>
    </div>
  );
}

// AutoPublishToggle — single switch on the brand overview. When ON,
// bulk-generated posts skip the Drafts review and go straight to
// 'scheduled' at their suggested time. Optimistic toggle; rolls
// back the local state if the PATCH fails. v0.1.67.
function AutoPublishToggle({ brand, tr }: { brand: Brand; tr: boolean }) {
  const qc = useQueryClient();
  const [local, setLocal] = useState<boolean>(brand.auto_publish ?? false);
  useEffect(() => {
    setLocal(brand.auto_publish ?? false);
  }, [brand.auto_publish]);
  const m = useMutation({
    mutationFn: (next: boolean) =>
      api<Brand>(`/brands/${brand.id}`, {
        method: "PATCH",
        body: { auto_publish: next },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["brand", brand.id] });
      void qc.invalidateQueries({ queryKey: ["brands"] });
    },
    onError: () => {
      // Roll back the optimistic switch.
      setLocal(brand.auto_publish ?? false);
    },
  });
  const toggle = () => {
    const next = !local;
    setLocal(next);
    m.mutate(next);
  };
  return (
    <div
      className="card"
      style={{ padding: 16, display: "flex", alignItems: "flex-start", gap: 12 }}
    >
      <div style={{ flex: 1 }}>
        <div className="mono" style={{ fontSize: 9, color: "var(--mu)", letterSpacing: "0.16em", textTransform: "uppercase", marginBottom: 4 }}>
          {tr ? "İş akışı" : "Workflow"}
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
          {tr ? "Otomatik yayınla" : "Auto-publish"}
        </div>
        <div style={{ fontSize: 11, color: "var(--tx2)", lineHeight: 1.45 }}>
          {tr
            ? "Toplu üretilen gönderiler taslakta beklemeden, önerilen zamana göre otomatik planlanır."
            : "Bulk-generated posts skip Drafts review and auto-schedule at the suggested time."}
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={local}
        onClick={toggle}
        disabled={m.isPending}
        style={{
          width: 36,
          height: 20,
          borderRadius: 999,
          border: 0,
          background: local ? "var(--accent)" : "var(--b2)",
          position: "relative",
          cursor: m.isPending ? "wait" : "pointer",
          transition: "background 0.15s",
          flexShrink: 0,
          marginTop: 2,
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 2,
            left: local ? 18 : 2,
            width: 16,
            height: 16,
            borderRadius: "50%",
            background: "#fff",
            transition: "left 0.15s",
          }}
        />
      </button>
    </div>
  );
}
